/**
 * useAnonymousId Hook
 *
 * Generates a unique anonymous user ID based on device characteristics.
 * Same device will always return the same ID.
 * The generated ID has format: "anonymous_" + hash, with maximum length of 36 characters.
 * Uses caching to avoid repeated calculations across multiple components.
 *
 * @returns {string} Generated anonymous user ID (max 36 chars)
 */

// Cache the anonymous ID to avoid repeated calculations
let cachedAnonymousId = null

export const useAnonymousId = () => {
    // Return cached value if available
    if (cachedAnonymousId) {
        return cachedAnonymousId
    }

    // Generate a simple device fingerprint based on stable device characteristics
    const generateDeviceFingerprint = () => {
        const fingerprints = []

        // Use navigator.platform as it's more stable than User Agent OS info
        fingerprints.push(navigator.platform)
        fingerprints.push(new Date().getTimezoneOffset().toString())

        // Hardware info (if available)
        if (navigator.hardwareConcurrency) {
            fingerprints.push(`cores:${navigator.hardwareConcurrency}`)
        }

        // Add screen resolution as additional stable identifier
        if (window.screen && window.screen.width && window.screen.height) {
            fingerprints.push(
                `screen:${window.screen.width}x${window.screen.height}`
            )
        }

        // Add color depth if available
        if (window.screen && window.screen.colorDepth) {
            fingerprints.push(`color:${window.screen.colorDepth}`)
        }

        // Add pixel ratio for better device identification
        if (window.devicePixelRatio) {
            fingerprints.push(`ratio:${window.devicePixelRatio}`)
        }

        // Remove User Agent OS parsing as navigator.platform is more stable

        return fingerprints.join('|')
    }

    // Generate simple hash from fingerprint
    const simpleHash = (str) => {
        let hash = 0
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i)
            hash = (hash << 5) - hash + char
            hash = hash & hash
        }
        return Math.abs(hash).toString(36)
    }

    // Generate anonymous ID from fingerprint
    const generateAnonymousId = (deviceFingerprint) => {
        if (!deviceFingerprint) {
            return 'anonymous'
        }

        const hash = simpleHash(deviceFingerprint)
        // Ensure total length <= 36
        const anonymousId = 'anonymous_' + hash.slice(0, 25)
        return anonymousId
    }

    // Generate device fingerprint and create anonymous ID
    const deviceFingerprint = generateDeviceFingerprint()
    cachedAnonymousId = generateAnonymousId(deviceFingerprint)

    return cachedAnonymousId
}

export default useAnonymousId
